import useRouter from '../../../hooks/useRouter'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { TABS_OPTIONS, TABS_OPTIONS_CP } from './constants'
import RadioGroup from '../../../components/RadioGroup/RadioGroup'
import { ROLES } from '../../Permission'
import { TABS_OPTIONS_KEY } from '../MyOrganization/constants'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../redux/store/user/getters'
import { CommunityDetailsContainer } from './CommunityDetails/CommunityDetailsContainer'
import { CommunityUsers } from './CommunityUsers/CommunityUsers'
import { HealthSystems } from './HealthSystems/HealthSystems'
import MyOrganizationCommunityAbilityProvider from './Providers/MyOrganizationAbilityProvider'
import { BankingSetupContainer } from './BankingSetup/BankingSetupContainer'
import {
  setLoading,
  updateLoadingStack
} from '../../../redux/store/common/slice'
import { getCommunityDetails } from '../Communities/CommunityDetails/api'
import { getBusinessAccountDetailsRequest } from './BankingSetup/api'
import { FETCH_STATUS } from 'redux/store/common/types'
import { TBusinessAccountDetails } from './BankingSetup/types'
import { CommunityData } from '../Communities/CommunityDetails/types'
import { MOOV_BUSINESS_ACCOUNT_STATUS } from './BankingSetup/constants'
import { BankAccountContainer } from 'features/BankAccount/BankAccountContainer'

export const MyOrganizationCommunity = () => {
  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const { query, push } = useRouter()
  const [activeSubTab, setActiveSubTab] = useState(
    query?.type || TABS_OPTIONS[0].value
  )
  const [communityData, setCommunityData] = useState<CommunityData | null>(null)
  const [businessAccountDetails, setBusinessAccountDetails] = useState<
    TBusinessAccountDetails | undefined
  >(undefined)

  const getCommunityData = useCallback(async () => {
    try {
      dispatch(setLoading(true))
      const response = await getCommunityDetails(user.community, {})
      setCommunityData(response.data)
    } finally {
      dispatch(setLoading(false))
    }
  }, [dispatch, user.community])

  useEffect(() => {
    getCommunityData()
  }, [getCommunityData])

  const handleTabs = (e) => {
    setActiveSubTab(e)
    push(e)
  }
  const tabsOptions = useMemo(() => {
    if (user.role === ROLES.COMMUNITY_PRESIDENT) {
      return TABS_OPTIONS_CP.map((tab) => {
        return {
          label: tab.label,
          value: tab.value,
          disabled:
            tab.value === 'bank_accounts' &&
            businessAccountDetails?.verification_status !==
              MOOV_BUSINESS_ACCOUNT_STATUS.VERIFIED
        }
      })
    } else return TABS_OPTIONS
  }, [businessAccountDetails?.verification_status, user.role])

  const getBusinessAccountDetails = useCallback(async () => {
    if (!communityData?.moov_business_account) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getBusinessAccountDetailsRequest(
        communityData.moov_business_account
      )
      setBusinessAccountDetails(res.data)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [communityData?.moov_business_account, dispatch])

  const refreshDetails = useCallback(() => {
    getCommunityData()
    getBusinessAccountDetails()
  }, [getCommunityData, getBusinessAccountDetails])

  useEffect(() => {
    if (communityData?.moov_business_account) {
      getBusinessAccountDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityData?.moov_business_account])

  return (
    <MyOrganizationCommunityAbilityProvider>
      <div className="my-organisation-container">
        <RadioGroup.Buttons
          options={tabsOptions}
          defaultValue={activeSubTab}
          onChange={handleTabs}
          className="mt-0 mb-24"
        />
        {activeSubTab === TABS_OPTIONS_KEY.general && (
          <CommunityDetailsContainer communityData={communityData} />
        )}
        {activeSubTab === TABS_OPTIONS_KEY.users && <CommunityUsers />}
        {activeSubTab === TABS_OPTIONS_KEY.health_systems && <HealthSystems />}
        {activeSubTab === TABS_OPTIONS_KEY.banking_setup && (
          <BankingSetupContainer
            businessAccountDetails={businessAccountDetails}
            refreshDetails={refreshDetails}
            moovBusinessAccountId={communityData?.moov_business_account}
            communityEntity={{
              entity_id: user?.community || '',
              entity_type: 'community'
            }}
          />
        )}
        {activeSubTab === TABS_OPTIONS_KEY.bank_accounts &&
          businessAccountDetails && (
            <BankAccountContainer
              businessDetails={businessAccountDetails}
              getBusinessDetails={getBusinessAccountDetails}
            />
          )}
      </div>
    </MyOrganizationCommunityAbilityProvider>
  )
}
